import React, { useRef, useEffect } from 'react';
import { cssRgb, type ConstellationId } from '@/lib/skins';

/**
 * Full-screen constellation layer over the animated background: one figure
 * (Virgo, Orion, Ursa Major…) or just the floating dust, joined to the finger
 * or cursor by faint lines. Each slide turns and zooms the figure a little.
 *
 *   motion      — 0 freezes drift and twinkle (the figure still eases between slides)
 *   density     — amount of floating dust (0.25–2)
 *   interactive — tapping pushes the dust away and sends out a ring
 *
 * 'none' renders nothing.
 */
export interface ConstellationProps {
  activeSlide?: number;
  figure?: ConstellationId;
  motion?: number;
  density?: number;
  interactive?: boolean;
}

type Star = { x: number; y: number; r: number; glow: number };
type Figure = { stars: Star[]; lines: [number, number][] };

const s = (x: number, y: number, r: number, glow = r * 4.5): Star => ({ x, y, r, glow });

/** Relative coordinates (0–1); the brightest star of each figure gets the biggest glow. */
export const FIGURES: Record<Exclude<ConstellationId, 'none' | 'particles'>, Figure> = {
  virgo: {
    stars: [
      s(0.65, 0.58, 3.0, 18), s(0.48, 0.40, 2.2, 12), s(0.76, 0.26, 2.2, 12), s(0.62, 0.32, 1.8, 9), s(0.34, 0.42, 1.8, 9),
      s(0.20, 0.36, 2.0, 9), s(0.46, 0.56, 2.0, 10), s(0.36, 0.67, 1.6, 8), s(0.27, 0.74, 1.3, 6), s(0.18, 0.80, 1.5, 6),
      s(0.56, 0.48, 1.6, 8), s(0.74, 0.66, 1.8, 8), s(0.84, 0.72, 1.5, 6),
    ],
    lines: [[5, 4], [4, 1], [1, 3], [3, 2], [1, 10], [10, 0], [0, 6], [6, 7], [7, 8], [8, 9], [0, 11], [11, 12]],
  },
  orion: {
    // Betelgeuse, Bellatrix, Alnitak, Alnilam, Mintaka, Saiph, Rigel, Meissa, sword
    stars: [
      s(0.30, 0.24, 2.8, 16), s(0.68, 0.28, 2.2, 11), s(0.42, 0.52, 2.0, 9), s(0.50, 0.50, 2.1, 10), s(0.58, 0.48, 1.9, 9),
      s(0.36, 0.80, 2.0, 9), s(0.72, 0.78, 2.8, 16), s(0.48, 0.14, 1.5, 6), s(0.49, 0.62, 1.3, 6),
    ],
    lines: [[7, 0], [7, 1], [0, 2], [1, 4], [2, 3], [3, 4], [2, 5], [4, 6], [3, 8]],
  },
  ursa: {
    // Big Dipper (Dubhe … Alkaid) plus Muscida and the front legs
    stars: [
      s(0.70, 0.30, 2.4, 12), s(0.72, 0.46, 2.2, 11), s(0.56, 0.50, 2.0, 9), s(0.53, 0.36, 1.6, 7), s(0.40, 0.33, 2.3, 11),
      s(0.28, 0.30, 2.1, 10), s(0.15, 0.38, 2.2, 11), s(0.86, 0.24, 1.4, 6), s(0.60, 0.70, 1.4, 6), s(0.80, 0.68, 1.4, 6),
    ],
    lines: [[0, 1], [1, 2], [2, 3], [3, 0], [3, 4], [4, 5], [5, 6], [0, 7], [2, 8], [1, 9]],
  },
  cassiopeia: {
    stars: [s(0.16, 0.40, 2.0, 10), s(0.33, 0.58, 2.3, 12), s(0.50, 0.44, 2.6, 14), s(0.67, 0.60, 2.2, 11), s(0.84, 0.38, 2.0, 10)],
    lines: [[0, 1], [1, 2], [2, 3], [3, 4]],
  },
  scorpius: {
    // Graffias, Dschubba, π, σ, Antares, τ, ε, μ, ζ, η, θ, ι, κ, Shaula
    stars: [
      s(0.64, 0.18, 1.8, 8), s(0.60, 0.27, 2.0, 9), s(0.62, 0.35, 1.5, 6), s(0.50, 0.33, 1.5, 6), s(0.42, 0.40, 3.0, 18),
      s(0.37, 0.47, 1.5, 6), s(0.34, 0.57, 1.8, 8), s(0.33, 0.67, 1.6, 7), s(0.37, 0.77, 1.7, 7), s(0.45, 0.84, 1.6, 7),
      s(0.56, 0.86, 2.0, 9), s(0.66, 0.82, 1.7, 7), s(0.71, 0.74, 1.8, 8), s(0.77, 0.69, 2.4, 12),
    ],
    lines: [[0, 1], [1, 2], [1, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 8], [8, 9], [9, 10], [10, 11], [11, 12], [12, 13]],
  },
  leo: {
    // Regulus, η, Algieba, Adhafera, Rasalas, ε, Zosma, Denebola, Chertan
    stars: [
      s(0.64, 0.64, 2.8, 16), s(0.62, 0.52, 1.6, 7), s(0.63, 0.40, 2.2, 11), s(0.67, 0.29, 1.6, 7), s(0.75, 0.22, 1.5, 6),
      s(0.81, 0.31, 1.8, 8), s(0.36, 0.40, 2.0, 9), s(0.17, 0.48, 2.4, 12), s(0.38, 0.56, 1.8, 8),
    ],
    lines: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [2, 6], [6, 7], [7, 8], [8, 0], [6, 8]],
  },
  crux: {
    // Southern Cross plus the two Pointers (α and β Centauri)
    stars: [
      s(0.48, 0.80, 2.8, 16), s(0.46, 0.22, 2.4, 13), s(0.68, 0.46, 2.5, 13), s(0.28, 0.42, 2.0, 9), s(0.40, 0.58, 1.3, 6),
      s(0.88, 0.72, 2.7, 15), s(0.93, 0.56, 2.5, 13),
    ],
    lines: [[0, 1], [2, 3], [5, 6]],
  },
};

const SLIDE_PARAMS = [
  { angle: 0, scale: 1.0, x: 0, y: 0 }, // Hero
  { angle: Math.PI / 4, scale: 0.85, x: 0.1, y: -0.1 }, // Gallery
  { angle: -Math.PI / 6, scale: 1.15, x: -0.1, y: 0.1 }, // Channels
  { angle: Math.PI / 2, scale: 0.9, x: 0.05, y: 0.05 }, // Links
  { angle: -Math.PI / 3, scale: 1.05, x: -0.05, y: -0.05 }, // Contact
  { angle: Math.PI * 0.6, scale: 0.95, x: -0.12, y: 0.08 }, // About
  { angle: -Math.PI * 0.45, scale: 1.1, x: 0.08, y: -0.12 }, // VIP
  { angle: Math.PI * 0.8, scale: 1.0, x: 0.0, y: 0.1 }, // Subscribe
  { angle: -Math.PI * 0.8, scale: 0.85, x: -0.08, y: -0.08 }, // Help
  { angle: Math.PI * 0.15, scale: 1.0, x: 0.06, y: 0.06 }, // Profile
  { angle: -Math.PI * 0.15, scale: 0.9, x: -0.06, y: 0.12 }, // Register
  { angle: Math.PI * 0.35, scale: 1.05, x: 0.04, y: -0.04 }, // Wishlist
];

export const Constellation: React.FC<ConstellationProps> = ({ activeSlide = 0, figure = 'virgo', motion = 1, density = 1, interactive = true }) => {
  const ref = useRef<HTMLCanvasElement>(null);
  const activeSlideRef = useRef(activeSlide);
  const motionRef = useRef(motion);

  useEffect(() => { activeSlideRef.current = activeSlide; }, [activeSlide]);
  useEffect(() => { motionRef.current = motion; }, [motion]);

  useEffect(() => {
    if (figure === 'none') return;
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const touch = interactive && !reduced;
    const shape = figure === 'particles' ? null : FIGURES[figure];
    const stars = shape?.stars ?? [];
    const lines = shape?.lines ?? [];

    // Skin colours, re-read now and then so the customizer recolours the layer live
    let ink = cssRgb('--c-tint', '255, 255, 255');
    let brand = cssRgb('--c-crimson-rgb', '220, 57, 75');
    let lastColours = 0;

    let w = 0;
    let h = 0;
    const count = Math.round((figure === 'particles' ? 70 : 50) * Math.min(2, Math.max(0.25, density)));
    const particles = Array.from({ length: count }, () => ({
      x: Math.random() * 2000,
      y: Math.random() * 2000,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      // Extra velocity from taps, decays back to the drift
      kx: 0,
      ky: 0,
      r: 0.5 + Math.random() * 1.2,
      phase: Math.random() * Math.PI * 2,
      twinkleSpeed: 0.8 + Math.random() * 3.0,
    }));
    const rings: { x: number; y: number; t: number }[] = [];

    let current = { ...SLIDE_PARAMS[0] };
    const pointer = { x: -999, y: -999, active: false, until: 0 };

    const local = (e: PointerEvent) => {
      const r = c.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top, inside: e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom };
    };
    const onMove = (e: PointerEvent) => {
      const p = local(e);
      if (!p.inside) { pointer.active = false; return; }
      pointer.x = p.x; pointer.y = p.y;
      // Touch keeps the focus for a moment after the finger lifts
      pointer.active = e.pointerType === 'mouse' || e.buttons > 0;
      if (pointer.active) pointer.until = performance.now() + 1200;
    };
    const onDown = (e: PointerEvent) => {
      const p = local(e);
      if (!p.inside) return;
      pointer.x = p.x; pointer.y = p.y; pointer.active = true; pointer.until = performance.now() + 1200;
      rings.push({ x: p.x, y: p.y, t: performance.now() });
      if (rings.length > 4) rings.shift();
      // Push the dust away from the tap
      for (const q of particles) {
        const dx = q.x - p.x;
        const dy = q.y - p.y;
        const d = Math.hypot(dx, dy) || 1;
        if (d < 180) {
          const f = (1 - d / 180) * 6;
          q.kx += (dx / d) * f;
          q.ky += (dy / d) * f;
        }
      }
    };
    const onLeave = () => { pointer.active = false; };
    if (touch) {
      window.addEventListener('pointermove', onMove, { passive: true });
      window.addEventListener('pointerdown', onDown, { passive: true });
      document.documentElement.addEventListener('pointerleave', onLeave);
    }

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = c.offsetWidth;
      h = c.offsetHeight;
      c.width = w * dpr;
      c.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      for (const p of particles) {
        p.x = Math.random() * w;
        p.y = Math.random() * h;
      }
    };
    const ro = new ResizeObserver(resize);
    ro.observe(c);
    resize();

    let raf = 0;
    let clock = 0;
    let last = performance.now();

    const draw = () => {
      const now = performance.now();
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const speed = reduced ? 0 : Math.max(0, motionRef.current);
      clock += dt * speed;
      const time = clock;

      if (now - lastColours > 500) {
        ink = cssRgb('--c-tint', '255, 255, 255');
        brand = cssRgb('--c-crimson-rgb', '220, 57, 75');
        lastColours = now;
      }

      ctx.clearRect(0, 0, w, h);

      // 1. Floating dust (drift scaled by motion; tap kicks fade out)
      for (const p of particles) {
        p.x += p.vx * speed + p.kx;
        p.y += p.vy * speed + p.ky;
        p.kx *= 0.92;
        p.ky *= 0.92;
        if (p.x < 0) { p.x = 0; p.vx = Math.abs(p.vx); p.kx = Math.abs(p.kx); }
        if (p.x > w) { p.x = w; p.vx = -Math.abs(p.vx); p.kx = -Math.abs(p.kx); }
        if (p.y < 0) { p.y = 0; p.vy = Math.abs(p.vy); p.ky = Math.abs(p.ky); }
        if (p.y > h) { p.y = h; p.vy = -Math.abs(p.vy); p.ky = -Math.abs(p.ky); }
        const twinkle = speed > 0 ? 0.12 + 0.88 * ((Math.sin(time * p.twinkleSpeed + p.phase) + 1) * 0.5) : 0.6;
        ctx.fillStyle = `rgba(${ink}, ${twinkle * 0.55})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // 2. Focus: the finger/cursor, or a slow wander when nobody touches (none when still)
      const touching = pointer.active || now < pointer.until;
      let focus: { x: number; y: number } | null = null;
      if (touching) focus = { x: pointer.x, y: pointer.y };
      else if (speed > 0) focus = { x: w * 0.5 + Math.sin(time * 0.4) * (w * 0.22), y: h * 0.5 + Math.cos(time * 0.3) * (h * 0.16) };

      // 3. Ease the figure towards the slide's pose
      const target = SLIDE_PARAMS[activeSlideRef.current] ?? SLIDE_PARAMS[0];
      current = {
        angle: current.angle + (target.angle - current.angle) * 0.05,
        scale: current.scale + (target.scale - current.scale) * 0.05,
        x: current.x + (target.x - current.x) * 0.05,
        y: current.y + (target.y - current.y) * 0.05,
      };

      const computed = stars.map((star, idx) => {
        const dx = Math.sin(time * 0.7 + idx * 1.5) * 4;
        const dy = Math.cos(time * 0.55 + idx * 2.2) * 4;
        const rx = star.x - 0.5;
        const ry = star.y - 0.5;
        const rotX = (rx * Math.cos(current.angle) - ry * Math.sin(current.angle)) * current.scale;
        const rotY = (rx * Math.sin(current.angle) + ry * Math.cos(current.angle)) * current.scale;
        return { px: (0.5 + rotX + current.x) * w + dx, py: (0.5 + rotY + current.y) * h + dy, r: star.r, glow: star.glow };
      });

      // 4. Figure lines with a soft pulse
      const linePulse = speed > 0 ? 0.18 + 0.12 * Math.sin(time * 1.8) : 0.24;
      ctx.lineWidth = 1;
      ctx.strokeStyle = `rgba(${brand}, ${linePulse})`;
      for (const [a, b] of lines) {
        ctx.beginPath();
        ctx.moveTo(computed[a].px, computed[a].py);
        ctx.lineTo(computed[b].px, computed[b].py);
        ctx.stroke();
      }

      // 5. Short links between nearby points and to the focus
      const connectionDist = 150;
      const points = [...computed.map((p) => ({ x: p.px, y: p.py })), ...particles.map((p) => ({ x: p.x, y: p.y })), ...(focus ? [focus] : [])];
      const nStars = computed.length;
      for (let i = 0; i < points.length; i++) {
        for (let j = i + 1; j < points.length; j++) {
          const d = Math.hypot(points[i].x - points[j].x, points[i].y - points[j].y);
          if (d >= connectionDist) continue;
          const alpha = 1 - d / connectionDist;
          const isStarPair = i < nStars || j < nStars;
          ctx.strokeStyle = `rgba(${brand}, ${alpha * (isStarPair ? 0.35 : 0.12)})`;
          ctx.lineWidth = isStarPair ? 0.8 : 0.5;
          ctx.beginPath();
          ctx.moveTo(points[i].x, points[i].y);
          ctx.lineTo(points[j].x, points[j].y);
          ctx.stroke();
        }
      }

      // 6. Tap rings
      for (let i = rings.length - 1; i >= 0; i--) {
        const age = (now - rings[i].t) / 1000;
        if (age > 1.2) { rings.splice(i, 1); continue; }
        ctx.strokeStyle = `rgba(${brand}, ${0.45 * (1 - age / 1.2)})`;
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.arc(rings[i].x, rings[i].y, 12 + age * 140, 0, Math.PI * 2);
        ctx.stroke();
      }

      // 7. Stars: halo + bright core, brighter near the focus
      for (const star of computed) {
        const near = focus ? Math.hypot(star.px - focus.x, star.py - focus.y) < 60 : false;
        const glow = star.glow + (near ? 6 : 0);
        const grad = ctx.createRadialGradient(star.px, star.py, 0, star.px, star.py, glow);
        grad.addColorStop(0, `rgba(${ink}, ${near ? 0.95 : 0.8})`);
        grad.addColorStop(0.2, `rgba(${brand}, ${near ? 0.55 : 0.35})`);
        grad.addColorStop(1, `rgba(${brand}, 0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(star.px, star.py, glow, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = `rgb(${ink})`;
        ctx.beginPath();
        ctx.arc(star.px, star.py, star.r * 0.6, 0, Math.PI * 2);
        ctx.fill();
      }

      raf = requestAnimationFrame(draw);
    };

    const onVisibility = () => {
      cancelAnimationFrame(raf);
      last = performance.now();
      if (!document.hidden) raf = requestAnimationFrame(draw);
    };
    document.addEventListener('visibilitychange', onVisibility);
    draw();

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVisibility);
      if (touch) {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerdown', onDown);
        document.documentElement.removeEventListener('pointerleave', onLeave);
      }
      ro.disconnect();
    };
  }, [figure, density, interactive]);

  if (figure === 'none') return null;

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className="absolute inset-0 w-full h-full block pointer-events-none"
      style={{ zIndex: 1 }}
    />
  );
};

export default Constellation;
